<template>
<div class="template">
    <div class="flex jc-sb ai-fe mb-3">
        <div class="flex-33 ai-c">
            <button
                title="Filters"
                type="button"
                :disabled="is_loading"
                :class="{ 'primary': show_filters }"
                @click="show_filters = !show_filters">
                <i class="material-icons">filter_list</i>
                <span v-if="show_filters">Hide Filters</span>
                <span v-else>Show Filters</span>
            </button>

            <button
                :disabled="last_row_count === 0 || is_loading_export"
                 data-cy="export-facilities-button"
                class="ml-3 icon"
                title="Export"
                @click="onExportData">
                <i class="material-icons">save_alt</i>
            </button>

            <img v-show="is_loading_export" class="va-m ml-3" src="@/assets/img/loading-black-32.gif" alt="loading" />

            <span v-show="export_download_path !== null" class="ml-3">
                <a data-cy="download-facilities-export" :href="export_download_path" target="_blank">Download</a>
                <button data-cy="clear-export-button" class="text ml-6" @click="export_filename = null">
                    <i class="material-icons">clear</i>
                </button>
            </span>
        </div>

        <div class="flex-33 ta-c">
            <vue-table-pagination
                ref="facilities_table_pagination"
                @vuetable-pagination:change-page="onChangePage">
            </vue-table-pagination>
        </div>

        <div class="flex-33 flex jc-fe ai-fe">
            <div class="mr-3" style="width: 300px;" data-cy="more-filters">
                <label>More Filters:</label>
                <select-input
                    :disabled="is_loading || is_loading_export"
                    :active-index="active_filter_index"
                    :choices="filter_options"
                    @change="onChangeFilter" />
            </div>

            <button title="Refresh" type="button" :disabled="is_loading" @click="onLoadData">
                <i class="material-icons">refresh</i>
            </button>
        </div>
    </div>

    <div v-show="show_filters" class="flex jc-sb mb-3">
        <div style="flex: 1 0 0;">
            <label for="filter-facility-number">Facility Name</label>
            <input id="filter-facility-number" type="text" v-model="filters.facility_number" />
        </div>

        <div class="ml-5" style="flex: 1 0 0;">
            <label for="filter-account-name">Account Name</label>
            <input id="filter-account-name" type="text" v-model="filters.account_name" />
        </div>

        <div class="ml-5" style="flex: 1 0 0;">
            <label for="filter-corporate-group">Corporate Group</label>
            <input id="filter-corporate-group" type="text" v-model="filters.corporate_group" />
        </div>

        <div class="ml-5" style="flex: 1 0 0;" data-cy="insurance-program-filter">
            <label>Insurance Program</label>
            <insurance-program-select @change="(key) => filters.insurance_program_key = key" />
        </div>

        <div class="ml-5" style="flex: 1 0 0;" data-cy="software-filter">
            <label for="filter-software">Software</label>
            <select-input
                :active-index="filter.software.index"
                :choices="software_choices"
                @change="onChangeSoftware"></select-input>
        </div>
    </div>

    <div class="table-wrapper">
        <vue-table ref="facilities_table"
            :class="['table-loads', { 'loading': is_loading }]"
            api-url="/api/facilities"
            :per-page="100"
            table-height="475px"
            :append-params="api_params"
            data-path="data"
            pagination-path="pagination"
            :fields="columns"
            :http-fetch="onHttpFetch"
            no-data-template="<span>No facilities to show.</span>"
            detail-row-component="facilities-table-detail-row"
            @vuetable:loading="is_loading = true"
            @vuetable:loaded="is_loading = false"
            @vuetable:load-success="onTableLoadSuccess"
            @vuetable:pagination-data="onPaginationData"
            @vuetable:row-clicked="onToggleDetailRow">
            <template slot="actions" slot-scope="props">
                <button class="tertiary mr-3 icon" type="button"
                    @click.stop="onAskDeleteFacility(props.rowData)">
                    <i class="material-icons">delete</i>
                </button>

                <button class="icon" type="button"
                    @click.stop="onEditFacility(props.rowData)">
                    <i class="material-icons">edit</i>
                </button>
            </template>

            <template slot="facility_number" slot-scope="props">
                <div class="flex ai-c jc-sb">
                    <div>
                        <span>{{ props.rowData.facility_number }}</span>

                        <span :class="['badge', { primary: props.rowData.insurance_program_key === 'aig' }]" data-cy="insurance-program-badge">
                            {{ props.rowData.insurance_program_label }}
                        </span>
                    </div>

                    <v-popover v-if="props.rowData.note !== null" trigger="hover">
                        <span class="success-text fs-09 cu-h">
                            <i class="material-icons">chat_bubble</i>
                        </span>

                        <span slot="popover">{{ props.rowData.note }}</span>
                    </v-popover>
                </div>
            </template>

            <template slot="confirmed_move_outs" slot-scope="props">
                <div v-if="props.rowData.confirmed_move_outs_date !== null" title="Confirmed Move-Outs">
                    <span class="primary-text">
                        <i class="material-icons">done</i>
                    </span>
                </div>
                <div v-else-if="props.rowData.confirmed_no_move_outs_date !== null" title="Confirmed No Move-Outs">
                    <span class="secondary-text">
                        <i class="material-icons">done</i>
                    </span>
                </div>
                <div v-else>
                    <button
                        :disabled="loading_confirm_facility_id === props.rowData.id"
                        class="small"
                        type="button"
                        @click="onCheckConfirmedStatus(props.rowData)">
                        Confirm
                    </button>
                </div>
            </template>

            <template slot="software" slot-scope="props">
                {{ props.rowData.software }}
            </template>

            <template slot="city" slot-scope="props">
                {{ props.rowData.city }}, {{ props.rowData.state }}
            </template>

            <template slot="can_tag_removal" slot-scope="props">
                <i v-if="props.rowData.can_tag_removal" class="success-text material-icons">
                    check_circle
                </i>
            </template>
        </vue-table>
    </div>

    <EditFacilityModal
        v-if="edit_facility_modal_open"
        :key="edit_facility.id"
        :facility="edit_facility"
        @close="onEditModalClose"
        @save="onLoadData" />
</div>
</template>

<script>
import Vue from "vue"
import http from "@/http"
import { debounce } from "lodash"
import { parseQueryParameters } from "@/utilities/path"
import { listFacilitiesColumns } from "@/utilities/columns"
import EditFacilityModal from "@/components/EditFacilityModal"

export default {
    components: {
        EditFacilityModal
    },

    data () {
        return {
            active_filter_index: 0,
            columns: listFacilitiesColumns,
            edit_facility: null,
            edit_facility_modal_open: false,
            export_filename: null,
            filter: {
                account_name: "",
                corporate_group: "",
                facility_number: "",
                city: "",
                software: {
                    index: 0,
                    value: ""
                }
            },
            filter_options: [
                { key: "total", label: "Show All", badge: null },
                { key: "doing_move_outs", label: "Move-Outs In Progress", badge: null },
                { key: "confirmed_move_outs", label: "Confirmed Move-Outs", badge: null },
                { key: "confirmed_no_move_outs", label: "Confirmed No Move-Outs", badge: null },
                { key: "divider-01", divider: true },
                { key: "opt_outs_threshold", label: "Opt-Outs Threshold", badge: null },
                { key: "provided_proof_threshold", label: "Provided Proof Threshold", badge: null },
                { key: "bad_address_threshold", label: "Bad Address Threshold", badge: null },
                { key: "moved_out_threshold", label: "Moved Out Threshold", badge: null },
                { key: "divider-02", divider: true },
                { key: "initial_letters_unsent", label: "Audit Letters Not Sent", badge: null },
                { key: "reminder_letters_unsent", label: "Reminder Letters Not Sent", badge: null },
                { key: "welcome_letters_unsent", label: "Welcome Letters Not Sent", badge: null }
            ],
            filters: {
                account_name: "",
                corporate_group: "",
                facility_number: "",
                insurance_program_key: "",
                software: ""
            },
            is_loading: false,
            is_loading_export: false,
            last_row_count: 0,
            loading_confirm_facility_id: null,
            show_filters: false,
            software_choices: [
                { key: "", label: "", badge: null },
                { key: "easystorage", label: "EasyStorage", badge: this.countForSoftware("easystorage") },
                { key: "syrasoft", label: "Syrasoft", badge: this.countForSoftware("syrasoft") },
                { key: "sitelink_web", label: "SiteLink - Web", badge: this.countForSoftware("sitelink_web") },
                { key: "storedge", label: "Storedge", badge: this.countForSoftware("storedge") }
            ],
            view_contacts_facility: null,
            view_contacts_modal_open: false
        }
    },

    computed: {
        api_params () {
            const params = {}

            if (this.active_filter_index > 0) {
                params.filter = this.filter_options[this.active_filter_index].key
            }

            if (this.filters.account_name && this.filters.account_name.length > 1) {
                params.account_name = this.filters.account_name
            }

            if (this.filters.corporate_group && this.filters.corporate_group.length > 1) {
                params.corporate_group = this.filters.corporate_group
            }

            if (this.filters.facility_number && this.filters.facility_number.length > 1) {
                params.facility_number = this.filters.facility_number
            }

            if (this.filters.software && this.filters.software.length !== 0) {
                params.software = this.filters.software
            }

            if (this.filters.insurance_program_key && this.filters.insurance_program_key.length !== 0) {
                params.insurance_program_key = this.filters.insurance_program_key
            }

            return params
        },

        export_download_path () {
            if (this.export_filename === null) {
                return null
            }

            let path = `${this.$http.defaults.baseURL}/api/exports/download`

            path += `?file_key=${this.export_filename}`
            path += "&filename=all_facilities"

            return path
        }
    },

    watch: {
        filters: {
            deep: true,
            handler: debounce(function (newValue) {
                // NOTE: we cannot use an arrow function here
                this.onLoadData()
            }, 500)
        }
    },

    created () {
        const query = parseQueryParameters()

        if (query.filter) {
            for (const index in this.filter_options) {
                if (this.filter_options[index].key === query.filter) {
                    this.active_filter_index = index
                }
            }
        }

        this.$eventManager.on("facility-archive-success", () => this.onLoadData())
    },

    mounted () {
        this.loadInsurancePrograms()
    },

    methods: {
        /**
         * Get the number of facilities for a given software.
         *
         * @param {string} software Software
         *
         * @return int
         */
        countForSoftware (software) {
            if (!this.filtered_facilities) {
                return null
            }

            return this.filtered_facilities.filter(facility => facility.software === software).length
        },

        async loadInsurancePrograms () {
            try {
                const { data } = await http("/api/insurance-programs")

                this.insurance_program_choices = [
                    { key: "", badge: null, label: "All" },
                    ...data.map((data) => {
                        return {
                            key: data.key,
                            badge: null,
                            label: data.label
                        }
                    })
                ]
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            }
        },

        /**
         * Show a confirmation dialog box before deleting the facility.
         *
         * @param {object} facility Facility data object
         */
        async onAskDeleteFacility (facility) {
            const confirmation = confirm(`Are you sure you wish to delete '${facility.facility_number}'?`)

            if (confirmation) {
                this.is_loading = true

                try {
                    await http.delete(`/api/facilities/${facility.id}`)

                    this.$eventManager.emit("add-toast", { message: `Facility ${facility.facility_number} has been deleted!` })

                    this.onLoadData()
                } catch (ex) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                } finally {
                    this.is_loading = false
                }
            }
        },

        onChangeFilter (index) {
            this.active_filter_index = index

            this.onLoadData()
        },

        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.facilities_table.changePage(page)
        },

        /**
         * When the user selects a different software, update the UI.
         *
         * @param {int} index Option index
         */
        onChangeSoftware (index) {
            const software = this.software_choices[index]

            this.filter.software.index = index

            if (software) {
                this.filters.software = this.software_choices[index].key
            }
        },

        async onCheckConfirmedStatus (facility) {
            try {
                this.loading_confirm_facility_id = facility.id

                const {
                    data: {
                        tenants_flagged: tenantsFlagged
                    }
                } = await http(`/api/facilities/${facility.id}/check-flagged`)

                const confirmation = confirm(`There are ${tenantsFlagged.toLocaleString()} tenants flagged for this facility. Continue?`)

                if (confirmation) {
                    await http.post(`/api/facilities/${facility.id}/confirm-move-outs`)

                    this.$refs.facilities_table.reload()
                }
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            } finally {
                this.loading_confirm_facility_id = null
            }
        },

        onEditFacility (facility) {
            this.edit_facility = { ...facility }

            this.edit_facility_modal_open = true
        },

        /**
         * Close the edit facility modal and unload the edit facility data.
         */
        onEditModalClose () {
            this.edit_facility_modal_open = false
            this.edit_facility = null
        },

        async onExportData () {
            this.is_loading_export = true

            const params = {}

            if (this.active_filter_index > 0) {
                params.filter = this.filter_options[this.active_filter_index].key
            }

            if (this.filters.account_name && this.filters.account_name.length > 1) {
                params.account_name = this.filters.account_name
            }

            if (this.filters.corporate_group && this.filters.corporate_group.length > 1) {
                params.corporate_group = this.filters.corporate_group
            }

            if (this.filters.facility_number && this.filters.facility_number.length > 1) {
                params.facility_number = this.filters.facility_number
            }

            if (this.filters.software && this.filters.software.length !== 0) {
                params.software = this.filters.software
            }

            if (this.filters.insurance_program && this.filters.insurance_program.length !== 0) {
                params.insurance_program_key = this.filters.insurance_program
            }

            try {
                const { data: response } = await http("/api/exports/facilities", { params })

                this.export_filename = response.filename
            } catch (ex) {
                console.dir(ex)

                if (ex.message) {
                    this.$eventManager.emit("add-toast", { type: "error", message: ex.message })
                } else {
                    this.$eventManager.emit("add-toast", { type: "error", message: ex })
                }
            } finally {
                this.is_loading_export = false
            }
        },

        /**
         * Make the table API call. This is used by the vue-table component when fetching data.
         *
         * @param {string} API call URL
         * @param {object} options
         *
         * @param {Promise}
         */
        onHttpFetch (url, options) {
            return http.get(url, options)
        },

        /**
         * Tell the table to fetch new data. Filters will be auto-applied.
         */
        onLoadData () {
            Vue.nextTick(() => {
                setTimeout(() => {
                    this.$refs.facilities_table.reload()
                }, 0)
            })
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param object paginationData Pagination data
         *
         * @return null
         */
        onPaginationData (paginationData) {
            this.$refs.facilities_table_pagination.setPaginationData(paginationData)
        },

        /**
         * Get the loaded record count when the table successfully loads data.
         *
         * @param {object} data Server response data object
         */
        onTableLoadSuccess ({ data: response }) {
            this.last_row_count = response.data.length

            for (const index in this.filter_options) {
                const key = this.filter_options[index].key

                if (response.stats.move_outs[key] !== undefined) {
                    this.filter_options[index].badge = response.stats.move_outs[key]
                } else if (response.stats.tenant_thresholds[key] !== undefined) {
                    this.filter_options[index].badge = response.stats.tenant_thresholds[key]
                } else if (response.stats.letters[key] !== undefined) {
                    this.filter_options[index].badge = response.stats.letters[key]
                }
            }
        },

        /**
         * Toggle the detail row for the clicked record.
         *
         * @param object data   Row data
         * @param Event  ev    Event object
         *
         * @return null
         */
        onToggleDetailRow (data, ev) {
            for (const index in ev.path) {
                // let columns prevent clicking from happening
                if (ev.path[index].classList.contains("no-click")) {
                    return
                }

                // don't check past the table element
                if (ev.path[index].classList.contains("vuetable")) {
                    break
                }
            }

            this.$refs.facilities_table.toggleDetailRow(data.id)
        }
    }
}
</script>

<style scoped>
.template /deep/ .floated.pagination {
    float: none;
}

.template >>> .prime {
    color: #7ed5ad;
    padding: 0 4px;
    cursor: help;
}
</style>



// WEBPACK FOOTER //
// src/components/list-facilities-page/ActiveFacilitiesTab.vue